import { EnergyType, Status } from '@essent/common';
import { getConsumptionValuesViewModel } from '@innogy/become-a-customer/shared';
import type { ConsumptionValuesVM } from '@innogy/become-a-customer/shared/interfaces';
import { createSelector } from '@ngrx/store';

import { getFlowIdHasError, getFlowIdString } from '../flow/flow-id.selectors';
import { getRetentionState } from '../retention-feature.selector';

export const getConsumptionState = createSelector(
  getRetentionState,
  (state) => state.consumption
);

export const getConsumptionEntry = createSelector(
  getConsumptionState,
  getFlowIdString,
  (state, flowId) => (flowId != null ? state.entities[flowId] : undefined)
);

export const getConsumption = createSelector(
  getConsumptionEntry,
  (entry) => entry?.entry
);

export const getConsumptionStatus = createSelector(
  getConsumptionEntry,
  (entry) => entry?.status
);

export const getConsumptionHasError = createSelector(
  getConsumptionStatus,
  getFlowIdHasError,
  (status, hasFlowIdError) => status === Status.ERROR || hasFlowIdError
);

export const isConsumptionLoading = createSelector(
  getConsumptionStatus,
  getConsumptionHasError,
  (status, hasError) =>
    (status === undefined ||
      status === Status.PENDING ||
      status === Status.IDLE) &&
    !hasError
);

export const getConsumptionValuesVM = createSelector(
  getConsumption,
  (entry): ConsumptionValuesVM | undefined =>
    getConsumptionValuesViewModel(entry)
);

export const getElectricityAndGasConsumption = createSelector(
  getConsumption,
  isConsumptionLoading,
  (energyConsumptions, loading) => {
    if (loading || !energyConsumptions?.length) {
      return undefined;
    }
    const electricityConsumptionEntry = energyConsumptions?.find(
      (entry) => entry.energyType === EnergyType.ELECTRICITY
    );

    const gasConsumptionEntry = energyConsumptions?.find(
      (entry) => entry.energyType === EnergyType.GAS
    );

    const electricityAnnualUsages =
      electricityConsumptionEntry?.standardAnnualUsages ?? [];

    const gasAnnualUsages = gasConsumptionEntry?.standardAnnualUsages ?? [];

    const electricityTariffMap: Record<
      string,
      keyof typeof electricityConsumption
    > = {
      supply_normal: 'consumptionPeak',
      supply_low: 'consumptionOffPeak',
      return_supply_normal: 'consumptionReturnPeak',
      return_supply_low: 'consumptionReturnOffPeak',
    };

    const electricityConsumption = {
      consumptionPeak: 0,
      consumptionOffPeak: 0,
      consumptionReturnPeak: 0,
      consumptionReturnOffPeak: 0,
    };

    for (const usage of electricityAnnualUsages) {
      const key = electricityTariffMap[usage.directionTariff];
      if (key) {
        electricityConsumption[key] = usage.reading ?? 0;
      }
    }

    return {
      electricity: electricityConsumption,
      gas: gasAnnualUsages[0]?.reading ?? 0, // Gas always has one reading value
    };
  }
);
